import type { ValidationIssue } from '../../errors'
import { assertObjectSchema } from './schema/assert-object-schema'
import { assertSupportedSchema } from './schema/assert-supported-schema'
import { type FieldPlan, planField } from './schema/field-plan'
import { type AnyZod, shapeEntries } from './schema/introspect'
import { normalizeField } from './schema/normalize'
import { type FailFn, parseSchema } from './schema/parse'
import type { NormalizedInput, SchemaSource, ZodSchema } from './types'

// A declared schema slot, as the runner works with it. Two things live here and nowhere else: the author's
// zod schema, and the operations the runner performs on it. Anything zod can already do is DELEGATED — this
// is a wrapper, never a re-implementation. `MethodParams` (inputs) and `ConfigParams` (config) extend it.
//
// Every slot is optional: an undeclared one produces an instance with no `scheme` that answers "no fields"
// and passes values through unparsed, so call sites never need an `if (params)` guard. But a slot that IS
// declared must be a z.object — the constructor enforces that, so an instance is never half-usable.

export abstract class Params {
  readonly scheme?: ZodSchema

  // Derived once per declared slot, at registration. The absence policy comes from probing the schema, which
  // is two `safeParse` calls we would rather not repeat on every invocation.
  #plans?: FieldPlan[]

  protected constructor(scheme?: unknown, readonly source: SchemaSource = {}) {
    // Declared ⇒ must be a z.object. Checked here so EVERY slot is covered by construction, and at
    // registration time rather than on some later invocation (see ./schema/assert-object-schema.ts).
    assertObjectSchema(scheme, source)
    // …and every field inside it must be something the runtime can publish, render and coerce. Same moment,
    // same reason: a declaration the runner cannot classify is a wrong contract, not a rendering nicety
    // (see ./schema/assert-supported-schema.ts).
    assertSupportedSchema(scheme, source)

    this.scheme = (scheme ?? undefined) as ZodSchema | undefined
  }

  // The declared field names. This is what a `dependsOn` is derived from.
  keys(): string[] {
    return this.fields().map(([name]) => name)
  }

  // The declared fields, in declaration order. Public: a renderer needs them as much as the pipeline does.
  fields(): [name: string, schema: AnyZod][] {
    return shapeEntries(this.scheme as AnyZod | undefined)
  }

  protected plans(): FieldPlan[] {
    this.#plans ??= this.fields().map(([name, schema]) => planField(name, schema))

    return this.#plans
  }

  /**
   * Normalize, coerce and validate a whole input object against the declared fields.
   *
   * Only declared fields appear in the output — an undeclared key is dropped, as it always was. Every
   * declared field appears, holding whatever it resolved to, which for an absent `.optional()` is
   * `undefined`: the key is there, so a handler asks `params.x === undefined` rather than `'x' in params`.
   * A field is omitted only when it FAILED, and then the caller is looking at `issues` instead.
   *
   * Every field is attempted even after one fails, so the caller can report the whole form at once instead
   * of making a user fix one field per attempt.
   */
  normalizeInput(input?: Record<string, unknown>): NormalizedInput {
    const source = input ?? {}
    const values: Record<string, unknown> = {}
    const issues: ValidationIssue[] = []

    for (const plan of this.plans()) {
      const result = normalizeField(source[plan.name], plan)

      if (result.issues.length) {
        issues.push(...result.issues)
      } else if (result.present) {
        values[plan.name] = result.value
      }
    }

    return { values, issues }
  }

  // Strict: required fields enforced, unknown stripped. For input the flow engine produces against the
  // published model, where a violation is a real contract break.
  parse<T>(value: unknown, fail: FailFn): T {
    return parseSchema<T>(this.scheme, value, fail)
  }

}
